import { Outlet, NavLink, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { notificationsAPI } from '../lib/api';
import {
  LayoutDashboard, Wallet, Clock, Bell, LogOut, Menu, X, ChevronDown, User,
  Send, ArrowDownLeft, Settings, ShieldCheck, ShieldAlert, Fingerprint,
  ArrowLeftRight, LifeBuoy, BarChart3, Crown, Users, Repeat, Key, Wallet as CreditIcon,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import { timeAgo } from '../lib/utils';
import NotificationPopup, { NotifTypeIcon } from './NotificationPopup';
import MobileBottomNav from './MobileBottomNav';
import { useNotificationWS } from '../hooks/useNotificationWS';
import '../App.css';

/* ─── Horizontal top-navbar items (flat, prioritized) ─── */
const clientNavItems = [
  { path: '/dashboard',    icon: LayoutDashboard, label: 'Главное меню' },
  { path: '/wallets',      icon: Wallet,          label: 'Счета' },
  { path: '/send',         icon: Send,            label: 'Отправить' },
  { path: '/receive',      icon: ArrowDownLeft,   label: 'Получить' },
  { path: '/exchange',     icon: ArrowLeftRight,  label: 'Обмен' },
  { path: '/transactions', icon: Clock,           label: 'История' },
];

const adminNavItems = [
  { path: '/admin',               icon: BarChart3, label: 'Панель',     end: true },
  { path: '/admin/users',         icon: Users,     label: 'Пользователи' },
  { path: '/admin/transactions',  icon: Repeat,    label: 'Транзакции' },
  { path: '/admin/wallets',       icon: Wallet,    label: 'Счета' },
  { path: '/admin/credits',       icon: CreditIcon,label: 'Кредиты' },
  { path: '/admin/notifications', icon: Bell,      label: 'Уведомления' },
  { path: '/admin/password',      icon: Key,       label: 'Смена пароля' },
  { path: '/admin/status',        icon: Settings,  label: 'Статус' },
];

/* ─── Top-nav pill (desktop) — gold underline indicator on active ─── */
function NavPill({ to, icon: Icon, label, end, onClick }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
      className={({ isActive }) =>
        `relative group flex items-center gap-1.5 px-2.5 lg:px-3 py-2 text-[12px] lg:text-[12.5px] font-medium tracking-tight rounded-md transition-colors whitespace-nowrap ${
          isActive
            ? 'text-white'
            : 'text-white/75 hover:text-white'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={14} strokeWidth={isActive ? 2.2 : 1.7} />
          <span className="hidden lg:inline">{label}</span>
          {isActive && (
            <motion.span
              layoutId="nav-underline"
              className="absolute -bottom-[14px] left-2 right-2 h-[2px] rounded-full bg-white"
              transition={{ type: 'spring', stiffness: 380, damping: 32 }}
            />
          )}
        </>
      )}
    </NavLink>
  );
}

/* ─── Mobile drawer link ─── */
function MobileLink({ to, icon: Icon, label, end, onClick }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      data-testid={`mob-drawer-${label.toLowerCase().replace(/\s/g, '-')}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-md text-[14px] tracking-tight transition-all ${
          isActive
            ? 'bg-brand/10 text-brand border-l-2 border-brand'
            : 'text-foreground/75 hover:bg-muted/50 border-l-2 border-transparent'
        }`
      }
    >
      <Icon size={16} strokeWidth={1.7} />
      <span className="font-medium">{label}</span>
    </NavLink>
  );
}

function VerifChip({ status }) {
  const map = {
    verified: { Icon: ShieldCheck, label: 'Верифицирован', cls: 'text-emerald-500/90' },
    pending:  { Icon: Fingerprint, label: 'На проверке',   cls: 'text-amber-500/90' },
  };
  const m = map[status] || { Icon: ShieldAlert, label: 'Не пройдена', cls: 'text-destructive/90' };
  const Icon = m.Icon;
  return (
    <NavLink
      to="/verification"
      data-testid="nav-verification-chip"
      className="hidden xl:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 transition-colors"
    >
      <Icon size={11} className="text-white" strokeWidth={2} />
      <span className="text-[10px] uppercase tracking-wider font-semibold text-white">{m.label}</span>
    </NavLink>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [expandedNotif, setExpandedNotif] = useState(null);

  const token = typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem('token') || localStorage.getItem('token')) : null;
  useNotificationWS(token);

  const fetchNotifications = useCallback(async () => {
    try { const { data } = await notificationsAPI.list(); setNotifications(data.notifications || []); setUnreadCount(data.unread_count || 0); } catch { /* noop */ }
  }, []);
  useEffect(() => { fetchNotifications(); const i = setInterval(fetchNotifications, 60000); return () => clearInterval(i); }, [fetchNotifications]);

  const markRead = async (id) => { try { await notificationsAPI.markRead(id); fetchNotifications(); } catch { /* noop */ } };
  const markAllRead = async () => { try { await notificationsAPI.markAllRead(); fetchNotifications(); } catch { /* noop */ } };

  const isAdmin = user?.is_admin;
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isVerificationRoute = location.pathname === '/verification';
  const needsVerification = !isAdmin && user?.verification_status === 'unverified';
  if (needsVerification && !isVerificationRoute) return <Navigate to="/verification" replace />;

  // Pick active nav set
  const navItems = isAdmin && isAdminRoute ? adminNavItems : clientNavItems;
  const showAdminSwitch = isAdmin;
  const vStatus = user?.verification_status || 'unverified';

  return (
    <div className="flex flex-col overflow-hidden" style={{ height: '100dvh', maxHeight: '-webkit-fill-available' }}>
      {/* ════════════════ HORIZONTAL TOP NAVBAR (bank red) ════════════════ */}
      <header
        className="hidden md:block sticky top-0 z-30 bg-brand text-white shadow-md"
        data-testid="top-navbar"
      >
        <div className="max-w-[1480px] mx-auto h-16 px-4 md:px-6 flex items-center gap-3 md:gap-6">
          {/* Mobile: hamburger */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden h-9 w-9 rounded-md text-white hover:bg-white/15"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu size={18} />
          </Button>

          {/* Brand */}
          <button
            onClick={() => navigate(isAdminRoute ? '/admin' : '/dashboard')}
            className="flex items-center gap-2.5 group flex-shrink-0"
            data-testid="brand-link"
          >
            <div className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 bg-[#0a1938]" aria-label="Зифора"><svg viewBox="0 0 64 64" className="w-[70%] h-[70%]" aria-hidden="true"><path d="M32 12 L52 22 L52 25 L12 25 L12 22 Z" fill="#fff"/><rect x="16" y="27" width="4" height="20" fill="#fff"/><rect x="24" y="27" width="4" height="20" fill="#fff"/><rect x="40" y="27" width="4" height="20" fill="#fff"/><rect x="48" y="27" width="4" height="20" fill="#fff"/><rect x="10" y="49" width="44" height="4" fill="#fff"/><text x="32" y="44" textAnchor="middle" fontFamily="Inter,Arial,sans-serif" fontWeight="900" fontSize="18" fill="#c8102e">З</text></svg></div>
            <div className="hidden sm:flex flex-col items-start leading-none">
              <span className="text-[16px] font-bold tracking-tight" data-testid="app-logo">Зифора</span>
              <span className="text-[9px] uppercase tracking-widest opacity-85 mt-0.5">Личный кабинет</span>
            </div>
          </button>

          {/* Center divider line */}
          <div className="hidden md:block h-7 w-px bg-white/25 mx-1" />

          {/* Desktop nav pills — only for CLIENT routes (admin uses left sidebar) */}
          {!isAdminRoute && (
            <nav className="hidden md:flex items-center gap-0.5 flex-1 min-w-0 overflow-x-auto no-scrollbar" data-testid="desktop-nav">
              {navItems.map(({ path, icon, label, end }) => (
                <NavPill key={path} to={path} icon={icon} label={label} end={end} />
              ))}
            </nav>
          )}
          {isAdminRoute && <div className="hidden md:block flex-1" />}

          <div className="flex-1 md:hidden" />

          {/* Right cluster */}
          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
            {!isAdminRoute && <VerifChip status={vStatus} />}

            {/* Admin toggle removed — admin now uses /admin-login separate portal */}

            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative h-9 w-9 rounded-md hover:bg-white/15 text-white" data-testid="notifications-btn">
                  <Bell size={16} strokeWidth={1.9} />
                  {unreadCount > 0 && (
                    <motion.span
                      initial={{ scale: 0 }} animate={{ scale: 1 }}
                      className="absolute -top-0.5 -right-0.5 w-4 h-4 text-[9px] rounded-full flex items-center justify-center font-bold bg-white text-brand"
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </motion.span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80" data-testid="notifications-dropdown">
                <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-brand">Уведомления</span>
                  {unreadCount > 0 && <button onClick={markAllRead} className="text-[11px] font-medium text-brand hover:underline" data-testid="mark-all-read">Прочитать все</button>}
                </div>
                <ScrollArea className="max-h-[60vh] sm:max-h-[420px]">
                  {notifications.length === 0 ? (
                    <div className="p-6 text-center text-sm text-muted-foreground">Нет уведомлений</div>
                  ) : (
                    notifications.slice(0, 15).map(n => (
                      <div
                        key={n.id}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpandedNotif(n); if (!n.read) markRead(n.id); }}
                        className={`flex flex-col gap-1 px-3 py-2.5 cursor-pointer hover:bg-muted/60 transition-colors border-b border-border/60 last:border-b-0 ${!n.read ? 'bg-brand/[0.04]' : ''}`}
                        data-testid={`notification-${n.id}`}
                      >
                        <div className="flex items-center gap-2 w-full">
                          <NotifTypeIcon type={n.type} size={13} />
                          <span className="text-[13px] font-semibold truncate flex-1 text-foreground">{n.title}</span>
                          <span className="text-[10px] text-muted-foreground font-mono whitespace-nowrap">{timeAgo(n.created_at)}</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground pl-5 line-clamp-2">{n.message}</p>
                      </div>
                    ))
                  )}
                </ScrollArea>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 pl-1.5 pr-2.5 h-9 rounded-md hover:bg-white/15 text-white" data-testid="user-menu-btn">
                  <div className="w-7 h-7 rounded-md bg-white text-brand flex items-center justify-center text-[11px] font-bold">
                    {user?.username?.[0]?.toUpperCase()}
                  </div>
                  <span className="hidden sm:inline text-[12.5px] font-medium tracking-tight">{user?.username}</span>
                  <ChevronDown size={11} className="hidden sm:inline opacity-80" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52" data-testid="user-menu-dropdown">
                <div className="px-3 py-2.5">
                  <p className="text-[13px] font-semibold tracking-tight">{user?.username}</p>
                  <p className="text-[10px] text-muted-foreground font-mono mt-0.5 truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer" data-testid="profile-menu-btn">
                  <User size={13} className="mr-2" />Профиль
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/profile?tab=settings')} className="cursor-pointer">
                  <Settings size={13} className="mr-2" />Настройки
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/support/help')} className="cursor-pointer">
                  <LifeBuoy size={13} className="mr-2" />Поддержка
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer">
                  <a href="https://t.me/Zyphora_asia" target="_blank" rel="noopener noreferrer" data-testid="user-menu-telegram" className="flex items-center">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="#229ED9" aria-hidden="true" className="mr-2 flex-shrink-0">
                      <path d="M9.04 16.5l-.36 5.06c.52 0 .74-.22 1.01-.49l2.43-2.32 5.04 3.69c.93.51 1.59.24 1.84-.86l3.34-15.65h.01c.29-1.36-.5-1.9-1.39-1.57L1.74 9.95C.41 10.47.43 11.23 1.51 11.56l4.95 1.54L18.6 6.06c.55-.36 1.05-.16.64.2L9.04 16.5z" />
                    </svg>
                    Telegram чат
                  </a>
                </DropdownMenuItem>
                {!isAdminRoute && (
                  <DropdownMenuItem onClick={() => navigate('/verification')} className="cursor-pointer">
                    <ShieldCheck size={13} className="mr-2" />Верификация
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-destructive cursor-pointer focus:text-destructive">
                  <LogOut size={13} className="mr-2" />Выйти
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>


      {/* ════════════════ MOBILE DRAWER ════════════════ */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 md:hidden"
              style={{ background: 'rgba(26, 26, 26, 0.4)', backdropFilter: 'blur(4px)' }}
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: -300 }} animate={{ x: 0 }} exit={{ x: -300 }}
              transition={{ type: 'spring', damping: 30, stiffness: 320 }}
              className="fixed left-0 top-0 bottom-0 w-[280px] z-50 flex flex-col md:hidden border-r border-border"
              style={{ background: '#ffffff' }}
              data-testid="mobile-drawer"
            >
              {/* Drawer header */}
              <div className="px-5 pt-5 pb-4 flex items-center justify-between border-b border-border bg-brand text-white">
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 bg-[#0a1938]" aria-label="Зифора"><svg viewBox="0 0 64 64" className="w-[70%] h-[70%]" aria-hidden="true"><path d="M32 12 L52 22 L52 25 L12 25 L12 22 Z" fill="#fff"/><rect x="16" y="27" width="4" height="20" fill="#fff"/><rect x="24" y="27" width="4" height="20" fill="#fff"/><rect x="40" y="27" width="4" height="20" fill="#fff"/><rect x="48" y="27" width="4" height="20" fill="#fff"/><rect x="10" y="49" width="44" height="4" fill="#fff"/><text x="32" y="44" textAnchor="middle" fontFamily="Inter,Arial,sans-serif" fontWeight="900" fontSize="18" fill="#c8102e">З</text></svg></div>
                  <div>
                    <p className="text-[16px] font-bold leading-none tracking-tight">Зифора</p>
                    <p className="text-[9px] uppercase tracking-widest opacity-85 mt-1">Личный кабинет</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setMobileOpen(false)} data-testid="close-mobile-menu">
                  <X size={16} />
                </Button>
              </div>

              {/* User card */}
              <button
                onClick={() => { navigate('/profile'); setMobileOpen(false); }}
                className="mx-3 mt-3 mb-2 px-3 py-2.5 rounded-md border border-border bg-white hover:border-brand/40 transition-all flex items-center gap-3 text-left"
                data-testid="mobile-profile-card"
              >
                <div className="w-9 h-9 rounded-md bg-brand text-white flex items-center justify-center text-[13px] font-bold flex-shrink-0">
                  {user?.username?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold truncate">{user?.username}</p>
                  <p className="text-[10px] text-muted-foreground truncate font-mono">{user?.email}</p>
                </div>
              </button>

              {/* Drawer nav */}
              <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto no-scrollbar" data-testid="mobile-nav">
                <p className="eyebrow px-3 mb-1.5 text-[9px]">{isAdminRoute ? 'Администрирование' : 'Кабинет'}</p>
                {navItems.map(({ path, icon, label, end }) => (
                  <MobileLink key={path} to={path} icon={icon} label={label} end={end} onClick={() => setMobileOpen(false)} />
                ))}

                {showAdminSwitch && !isAdminRoute && (
                  <>
                    <p className="eyebrow px-3 mb-1.5 text-[9px] mt-4">Служебный доступ</p>
                    <button
                      onClick={() => { navigate('/admin'); setMobileOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-md text-[14px] font-medium tracking-tight text-foreground/80 hover:bg-muted/50 border-l-2 border-transparent"
                      data-testid="mobile-admin-toggle"
                    >
                      <Crown size={16} strokeWidth={1.7} className="text-brand" />
                      <span>Админ-панель</span>
                    </button>
                  </>
                )}

                {!isAdminRoute && (
                  <>
                    <p className="eyebrow px-3 mb-1.5 text-[9px] mt-4">Аккаунт</p>
                    <MobileLink to="/profile" icon={User} label="Профиль" onClick={() => setMobileOpen(false)} />
                    <MobileLink to="/verification" icon={ShieldCheck} label="Верификация" onClick={() => setMobileOpen(false)} />
                    <MobileLink to="/support/help" icon={LifeBuoy} label="Поддержка" onClick={() => setMobileOpen(false)} />
                    <a
                      href="https://t.me/Zyphora_asia"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMobileOpen(false)}
                      data-testid="mob-drawer-telegram"
                      className="flex items-center gap-3 px-4 py-3 rounded-md text-[14px] tracking-tight transition-all text-foreground/75 hover:bg-muted/50 border-l-2 border-transparent"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="#229ED9" aria-hidden="true" className="flex-shrink-0">
                        <path d="M9.04 16.5l-.36 5.06c.52 0 .74-.22 1.01-.49l2.43-2.32 5.04 3.69c.93.51 1.59.24 1.84-.86l3.34-15.65h.01c.29-1.36-.5-1.9-1.39-1.57L1.74 9.95C.41 10.47.43 11.23 1.51 11.56l4.95 1.54L18.6 6.06c.55-.36 1.05-.16.64.2L9.04 16.5z" />
                      </svg>
                      <span className="font-medium">Telegram чат</span>
                    </a>
                  </>
                )}
              </nav>

              {/* Logout */}
              <div className="px-3 py-3 border-t border-border">
                <button
                  onClick={() => { setMobileOpen(false); logout(); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-md text-[14px] font-medium text-destructive hover:bg-destructive/10 transition-colors"
                  data-testid="mobile-logout"
                >
                  <LogOut size={16} strokeWidth={1.7} />
                  <span>Выйти</span>
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ════════════════ MAIN CONTENT ════════════════ */}
      <main className="flex-1 overflow-y-auto min-h-0 pb-24 md:pb-10" data-testid="main-content">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="p-3 sm:p-5 md:p-7 lg:p-10 max-w-[1480px] mx-auto w-full"
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {!isAdminRoute && <MobileBottomNav />}
      {!isAdmin && <NotificationPopup />}

      {/* Notification reader modal */}
      <AnimatePresence>
        {expandedNotif && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4"
            style={{ background: 'rgba(26, 26, 26, 0.35)', backdropFilter: 'blur(4px)' }}
            onClick={() => setExpandedNotif(null)} data-testid="notif-reader-overlay"
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 320, damping: 30 }}
              className="relative bg-card border border-border rounded-t-xl sm:rounded-xl shadow-2xl w-full sm:max-w-lg max-h-[85vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()} data-testid="notif-reader-modal"
            >
              <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border">
                <div className="flex items-start gap-2.5 min-w-0 flex-1">
                  <div className="mt-0.5"><NotifTypeIcon type={expandedNotif.type} size={18} /></div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold leading-tight text-foreground">{expandedNotif.title}</h3>
                    <p className="text-[11px] text-muted-foreground mt-1 font-mono">{new Date(expandedNotif.created_at).toLocaleString('ru-RU', { dateStyle: 'medium', timeStyle: 'short' })}</p>
                  </div>
                </div>
                <button onClick={() => setExpandedNotif(null)} className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-muted transition-colors flex-shrink-0" data-testid="notif-reader-close">
                  <X size={18} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="notif-reader-body">
                <p className="text-sm leading-relaxed whitespace-pre-wrap break-words text-foreground/85">{expandedNotif.message}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
